import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import {
  PromoteOptions,
  PromoteOptionsInterface,
  PromoteOptionsMapping,
} from "src/app/shared/enum/franchise/promoteOptions";
import { RoleOptionEnum } from "src/app/shared/enum/roleOptionEnum";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import { AdDetailsInterface } from "src/app/shared/models/advertising-campaigns/ad-details.interface";
import { SuccessDto } from "src/app/shared/models/success-dto";
import { BadgeService } from "src/app/shared/services/badge.service";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { JwtService } from "src/app/shared/services/jwt.service";
import { addCampaignSelector } from "src/app/store/advertisingCampaign/advertiserCampaign.selectors";
import {
  invokeAdDetails,
  invokeCreateAdvertisingCampaignByLocation,
  SetAddAdvertisingCampaign,
} from "src/app/store/advertisingCampaign/advertisingCampaign.actions";
import { AppStateInterface } from "src/app/store/appState.interface";
import { invokeFranchisedCompanyNamesByFranchise } from "src/app/store/franchise/franchise.action";
import { resultFranchisedCompanyNamesByFranchiseSelector } from "src/app/store/franchise/franchise.selectors";
import {
  catchForbiddenError,
  catchServerError,
} from "src/app/store/user/user.actions";
import {
  catchForbiddenErrorSelector,
  catchServerErrorSelector,
} from "src/app/store/user/user.selectors";
import { UtilFunctions } from "src/app/utils/utilFunctions";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-promote",
  templateUrl: "./promote.component.html",
  styleUrls: ["./promote.component.scss"],
})
export class PromoteComponent extends BaseComponent implements OnInit {
  bookMeeting = environment.links.bookMeetingLP;
  filename: string | null = null;
  loading: boolean;
  options: PromoteOptionsInterface[];

  selectedOption: PromoteOptions = PromoteOptions.ALL;

  promoteOptions = PromoteOptions;
  listFranchised$: Observable<string[]>;
  listFranchised: string[] = [];
  selectedFranchised: string[] = [];

  companyName: string;

  resultCreateAdvertisingCampaign$: Observable<SuccessDto | null>;
  catchServerError$: Observable<boolean | null>;
  catchForbiddenError$: Observable<boolean | null>;
  ad: AdDetailsInterface;
  constructor(
    public badgeService: BadgeService,
    private store: Store<AppStateInterface>,
    private jwtService: JwtService,
    private saveHistory: SaveUserHistoryService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router
  ) {
    super(store);
    this.companyName = this.jwtService.getCompanyName();
    this.resultCreateAdvertisingCampaign$ = this.store
      .pipe(select(addCampaignSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.catchServerError$ = this.store
      .pipe(select(catchServerErrorSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.catchForbiddenError$ = this.store
      .pipe(select(catchForbiddenErrorSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.listFranchised$ = this.store
      .pipe(select(resultFranchisedCompanyNamesByFranchiseSelector))
      .pipe(takeUntil(this.ngDestroyed$));
  }

  ngOnInit(): void {
    this.store.dispatch(invokeFranchisedCompanyNamesByFranchise({companyName : this.companyName}));
    this.listFranchised$.subscribe((result) => {
      if (result) {
        this.listFranchised = result;
        this.selectedFranchised = this.listFranchised;
      }
    });
    this.catchServerError$.subscribe((result) => {
      if (result) {
        this.loading = false;
        this.store.dispatch(catchServerError({ serverError: null }));
      }
    });
    this.catchForbiddenError$.subscribe((result) => {
      if (result) {
        this.loading = false;
        this.store.dispatch(catchForbiddenError({ forbiddenError: null }));
      }
    });
    this.options = Object.keys(PromoteOptions)
      .filter((value) => isNaN(Number(value)) === true)
      .map((key: string | any) => ({
        id: PromoteOptions[key as keyof typeof PromoteOptions],
        value: PromoteOptionsMapping[key as keyof typeof PromoteOptions],
      }));
    this.resultCreateAdvertisingCampaign$.subscribe(
      (result: SuccessDto | null) => {
        if (result != null) {
          this.loading = false;
          this.toastr.success(
            this.translate.instant("response.success." + result.message),
            this.translate.instant("response.successTitle")
          );
          this.store.dispatch(SetAddAdvertisingCampaign({ adCreated: null }));
          this.store.dispatch(
            invokeAdDetails({
              invokeAdDetails: { ...this.ad, videoFile: null },
            })
          );
          this.filename = null;
          this.router.navigate(["dashboard/franchise/locations/campaigns"]);
        }
      }
    );

    /// To clear the video store when returning to the Promote page
    this.store.dispatch(
      invokeAdDetails({ invokeAdDetails: { ...this.ad, videoFile: null } })
    );
  }

  catchFileName(name: string | null) {
    this.filename = name;
  }

  onSubmit() {
    this.loading = true;
    this.store.dispatch(
      invokeCreateAdvertisingCampaignByLocation({
        campaignDto: {
          title: UtilFunctions.replaceSpacesWithHyphen(this.companyName),
          locationPartners: this.selectedFranchised,
          videoLink: this.filename,
          franchiseCompanyName : this.companyName,
          optionEnum : RoleOptionEnum.FRANCHISE
        },
      })
    );
    this.saveHistory.saveUserHistory(
      UserHistoryActionsEnum.POST,
      UserHistoryServicesEnum.PROMOTE_CAMPAIGN_LP_BY_FRANCHISE
    );
  }

  save(event: any, option: PromoteOptions) {
    if (event.target.checked) {
      this.selectedOption = option;
    }
    if (this.selectedOption === PromoteOptions.CUSTOMIZED) {
      this.selectedFranchised = [];
    } else {
      this.selectedFranchised = this.listFranchised;
    }
  }

  addLocation(companyName: string) {
    if (!this.isItemIncluded(companyName)) {
      this.selectedFranchised = [...this.selectedFranchised, companyName];
    }
  }

  removeLocation(companyName: string) {
    this.selectedFranchised = this.selectedFranchised.filter(
      (item) => item !== companyName
    );
  }

  choseAction(companyName: string) {
    if (!this.isItemIncluded(companyName)) {
      this.addLocation(companyName);
    } else {
      this.removeLocation(companyName);
    }
  }

  isItemIncluded(item: string) {
    return this.selectedFranchised.includes(item);
  }
}
